import * as React from 'react';
import _ from 'lodash';
import { ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { COLOR_GRAY } from '../../constants/color-codes';
import { ENTITY_TYPE, USER_BASE_ROLE_TYPE, USER_ROLE_TYPE } from '../../app/enum/ENUM';
import { RenderTree } from '../../types/manage-users';
import RoleCustomTreeItem from './RoleCustomTreeItem';
import { Lock, LockOutlined } from '@material-ui/icons';

interface RoleTreeItemProps {
    data: RenderTree;
    index: number;
    setRoleTreeData: Function;
}

const RoleTreeItem: React.FunctionComponent<RoleTreeItemProps> = (props) => {

    const { t } = useLanguageLocale();

    const [records, setRecords] = React.useState<RenderTree>({} as RenderTree);

    React.useEffect(() => {
        setRecords(props.data);
    }, [props.data]);

    const getRoleType = (parentRole: number = USER_BASE_ROLE_TYPE.VIEWER, role: number, entityType: number): number | undefined => {
        if (
            parentRole === USER_BASE_ROLE_TYPE.VIEWER &&
            role === USER_BASE_ROLE_TYPE.ADMIN &&
            entityType == ENTITY_TYPE.BUILDING
        ) {
            return USER_ROLE_TYPE.EF_BLDG_ADMIN_ORG_VIEWER;
        } else if (
            parentRole === USER_BASE_ROLE_TYPE.VIEWER &&
            role === USER_BASE_ROLE_TYPE.ADMIN &&
            entityType == ENTITY_TYPE.SYSTEM
        ) {
            return USER_ROLE_TYPE.EF_SYSTEM_ADMIN_GATEWAY_VIEWER;
        } else if (
            parentRole === USER_BASE_ROLE_TYPE.LIMITED &&
            role === USER_BASE_ROLE_TYPE.ADMIN &&
            entityType == ENTITY_TYPE.BUILDING
        ) {
            return USER_ROLE_TYPE.EF_BLDG_ADMIN_ORG_VIEWER;
        } else if (
            parentRole === USER_BASE_ROLE_TYPE.LIMITED &&
            role === USER_BASE_ROLE_TYPE.VIEWER &&
            entityType == ENTITY_TYPE.BUILDING
        ) {
            return USER_ROLE_TYPE.EL_BLDG_VIEWER;
        } else if (
            parentRole === USER_BASE_ROLE_TYPE.LIMITED &&
            role === USER_BASE_ROLE_TYPE.ADMIN &&
            entityType == ENTITY_TYPE.SYSTEM
        ) {
            return USER_ROLE_TYPE.EF_SYSTEM_ADMIN_GATEWAY_VIEWER;
        } else if (
            parentRole === USER_BASE_ROLE_TYPE.LIMITED &&
            role === USER_BASE_ROLE_TYPE.VIEWER &&
            entityType == ENTITY_TYPE.SYSTEM
        ) {
            return USER_ROLE_TYPE.EF_SYSTEM_VIEWER;
        } else if (
            parentRole === USER_BASE_ROLE_TYPE.VIEWER &&
            role === USER_BASE_ROLE_TYPE.VIEWER &&
            entityType == ENTITY_TYPE.BUILDING
        ) {
            return USER_ROLE_TYPE.EL_BLDG_VIEWER;
        } else if (
            parentRole === USER_BASE_ROLE_TYPE.VIEWER &&
            role === USER_BASE_ROLE_TYPE.VIEWER &&
            entityType == ENTITY_TYPE.SYSTEM
        ) {
            return USER_ROLE_TYPE.EF_SYSTEM_VIEWER;
        } else {
            return undefined;
        }
    }

    const renderLabel = (label: any, record: any) => {
        return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>{getTreeViewItemLabel(label)}</span>
            <ToggleButtonGroup
                data-testid="toggle-group"
                orientation="horizontal"
                exclusive
                color="info"
                sx={{ height: 24 }}>
                <ToggleButton data-testid="toggle-limited" style={{ width: 80, textTransform: 'none' }} disabled={record.isLimitedDisabled === true || ENTITY_TYPE.SYSTEM == record.entityType || record.children.length === 0} selected={record.role === USER_BASE_ROLE_TYPE.LIMITED} onClick={(e) => handleSelectRole(e, record, USER_BASE_ROLE_TYPE.LIMITED)} value={USER_BASE_ROLE_TYPE.LIMITED}>
                    {
                        (record.isLimitedDisabled === true || ENTITY_TYPE.SYSTEM == record.entityType || record.children.length === 0) &&
                        <LockOutlined style={{ fontSize: 17 }} />
                    }
                    {
                        (!(record.isLimitedDisabled === true || ENTITY_TYPE.SYSTEM == record.entityType) && record.children.length > 0) &&
                        t('MANAGE_USERS.USER_ROLE_LIMITED')
                    }
                </ToggleButton>
                <ToggleButton data-testid="toggle-viewer" style={{ width: 80, textTransform: 'none' }} disabled={record.isViewerDisabled === true} selected={record.role === USER_BASE_ROLE_TYPE.VIEWER} onClick={(e) => handleSelectRole(e, record, USER_BASE_ROLE_TYPE.VIEWER)} value={USER_BASE_ROLE_TYPE.VIEWER}>
                    {t('MANAGE_USERS.USER_ROLE_VIEWER')}
                </ToggleButton>
                <ToggleButton data-testid="toggle-admin" style={{ width: 80, textTransform: 'none' }} selected={record.role === USER_BASE_ROLE_TYPE.ADMIN} onClick={(e) => handleSelectRole(e, record, USER_BASE_ROLE_TYPE.ADMIN)} value={USER_BASE_ROLE_TYPE.ADMIN}>
                    {t('MANAGE_USERS.USER_ROLE_ADMIN')}
                </ToggleButton>
            </ToggleButtonGroup>

        </div>;
    }

    const handleSelectRole = (event: React.MouseEvent<HTMLElement, MouseEvent>, record: any, role: number) => {

        const updateTree = (obj: any, id: string, role: number) => {
            if (obj.id === id) {

                obj.role = role;

                if (obj.children && obj.children.length > 0) {
                    obj.children = obj.children.map((child: any) => {
                        child.isLimitedDisabled = false;
                        child.isViewerDisabled = false;
                        return updateTree(child, child.id, role);
                    });
                } else {
                    obj.isLimitedDisabled = true;
                }

                if ((role === USER_BASE_ROLE_TYPE.ADMIN || role === USER_BASE_ROLE_TYPE.VIEWER) && obj.children) {
                    obj.children = obj.children.map((child: any) => {
                        child.isLimitedDisabled = true;
                        if (role === USER_BASE_ROLE_TYPE.ADMIN) {
                            child.isViewerDisabled = true;
                        }
                        return updateTree(child, child.id, child.role);
                    });
                }
                else if (obj.children) {
                    obj.children = obj.children.map((child: any) => {
                        return updateTree(child, child.id, child.role);
                    });
                }

                if (role === USER_BASE_ROLE_TYPE.LIMITED && obj.children) {
                    obj.children = obj.children.map((child: any) => {
                        child.role = undefined;
                        return updateTree(child, child.id, child.role);
                    });
                }

                return obj;
            }

            if (obj.children) {

                obj.children = obj.children.map((child: any) => updateTree(child, id, role));
            }

            return obj;
        };

        const update = updateTree(records, record.id, role);
        setRecords(update);

        const array: any = [];

        if (update.role && update.role !== USER_BASE_ROLE_TYPE.LIMITED) {
            array.push({
                entityId: records.id,
                entityType: records.entityType,
                grantAccessToBuilding: false,
                roleType: String(records.role),
                organizationId: '0',
                buildingId: ''
            })
        }

        const setData = (records: any) => {
            
            if ('children' in records && Array.isArray(records.children)) {
                records?.children.forEach((record: any) => {
                    const roleType = getRoleType(records.role, record.role, record.entityType);
                    if (roleType && record.role !== USER_BASE_ROLE_TYPE.LIMITED) {
                        array.push({
                            entityId: record.id,
                            entityType: record.entityType,
                            grantAccessToBuilding: false,
                            roleType: String(roleType),
                            organizationId: record.entityType == ENTITY_TYPE.BUILDING ? records.id : record.entityType == ENTITY_TYPE.SYSTEM ? records.parentId : '',
                            buildingId: record.entityType == ENTITY_TYPE.SYSTEM ? records.id : ''
                        });
                    }
                    if ('children' in record) {
                        setData(record);
                    }
                });
            }
        }
        
        setData(update);
        props.setRoleTreeData(array);
    };

    const getTreeViewItemLabel = (label: string) => {
        const values = label.split('#');
        return <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <Typography
                id="tree-item-name"
                data-testid="tree-item-name"
                style={{ color: COLOR_GRAY, fontWeight: 600, fontSize: 16, paddingLeft: 8, }} variant="subtitle1">
                {`${values[0]}`}
            </Typography>
            <Typography
                id="tree-item-name-sub"
                data-testid="tree-item-name-sub"
                style={{ alignSelf: 'left', color: COLOR_GRAY, fontWeight: 400, fontSize: 14, paddingLeft: 8, paddingRight: 24 }} variant="body2">
                {` (${values[1]})`}
            </Typography>
        </div>
    }

    const generateSimpleHash = (input: string) => {
        let hash = 0;
        for (let i = 0; i < input.length; i++) {
            const char = input.charCodeAt(i);
            hash = (hash << 5) - hash + char;
            hash |= 0; // Convert to 32bit integer
        }
        return hash.toString(16); // Convert to hexadecimal string
    }

    const CustomTreeRender = (data: RenderTree, index = String(props.index)) => {
        if ('id' in data) {
            const key = generateSimpleHash(data.id);
            const currentId = index ? `${index}-${index}` : index;
            if (data.children !== null) {
                return (
                    <RoleCustomTreeItem key={key} data={data} renderLabel={renderLabel} currentId={key} >
                        {data.children?.map((items, idx) => items ? CustomTreeRender(items, currentId) : '')}
                    </RoleCustomTreeItem>
                );
            }
            return <RoleCustomTreeItem key={key} data={data} renderLabel={renderLabel} currentId={key} />;
        }
    };

    return <>
        {
            CustomTreeRender(records as RenderTree)
        }
    </>;
};

export default RoleTreeItem;
